import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    FileText, 
    Plus, 
    Eye, 
    Filter,
    Fuel,
    Droplets,
    AlertTriangle,
    TrendingUp,
    TrendingDown,
    Gauge,
    Calendar,
    Lock,
    BarChart3,
    RefreshCw,
    Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

const API = `${window.location.origin}/api`;

const statusLabels = {
    normal: { label: 'Normal', class: 'badge-success', icon: '🟢' },
    atencao: { label: 'Atenção', class: 'badge-warning', icon: '🟡' },
    critico: { label: 'Crítico', class: 'badge-danger', icon: '🔴' }
};

const RDOPage = () => {
    const [rdos, setRdos] = useState([]);
    const [empurradores, setEmpurradores] = useState([]);
    const [viagens, setViagens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [consolidadoDialogOpen, setConsolidadoDialogOpen] = useState(false);
    const [selectedRDO, setSelectedRDO] = useState(null);
    const [consolidado, setConsolidado] = useState(null);
    const [graficoData, setGraficoData] = useState(null);
    const [projecao, setProjecao] = useState(null);
    const { hasRole } = useAuth();

    const [filters, setFilters] = useState({
        empurrador_id: '',
        data_inicio: '',
        data_fim: '',
        status_combustivel: ''
    });

    const [formData, setFormData] = useState({
        empurrador_id: '',
        viagem_id: '',
        conferente: '',
        data_lancamento: new Date().toISOString().split('T')[0],
        abastecimento_recebido: 0,
        tanque_armazenamento: 0,
        tanque_consumo: 0,
        consumo_diario: 0,
        agua_potavel: 0,
        consumo_agua_diario: 0,
        observacoes: '',
        observacao_variacao: '',
        is_novo_abastecimento: false
    });

    const fetchBaseData = useCallback(async () => {
        try {
            const [empRes, viagensRes] = await Promise.all([
                axios.get(`${API}/empurradores`),
                axios.get(`${API}/viagens`)
            ]);
            setEmpurradores(empRes.data);
            setViagens(viagensRes.data.filter(v => v.status === 'em_andamento'));
        } catch (error) {
            console.error('Erro ao carregar dados base:', error);
        }
    }, []);

    const fetchRDOs = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.empurrador_id && filters.empurrador_id !== 'all') params.empurrador_id = filters.empurrador_id;
            if (filters.data_inicio) params.data_inicio = filters.data_inicio;
            if (filters.data_fim) params.data_fim = filters.data_fim;
            if (filters.status_combustivel && filters.status_combustivel !== 'all') params.status_combustivel = filters.status_combustivel;

            const response = await axios.get(`${API}/rdos`, { params });
            setRdos(response.data);
        } catch (error) {
            toast.error('Erro ao carregar RDOs');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchBaseData();
    }, [fetchBaseData]);

    useEffect(() => {
        fetchRDOs();
    }, [fetchRDOs]);

    const fetchProjecao = async (empurradorId) => {
        try {
            const response = await axios.get(`${API}/rdos/projecao/${empurradorId}`);
            setProjecao(response.data);
        } catch (error) {
            console.error('Erro ao buscar projeção:', error);
        }
    };

    const fetchGrafico = async (empurradorId) => {
        try {
            const response = await axios.get(`${API}/rdos/grafico/${empurradorId}?dias=30`);
            setGraficoData(response.data);
        } catch (error) {
            console.error('Erro ao buscar dados do gráfico:', error);
        }
    };

    const fetchConsolidado = async () => {
        if (!filters.empurrador_id || !filters.data_inicio || !filters.data_fim) {
            toast.error('Selecione o empurrador e o período para gerar o consolidado');
            return;
        }
        try {
            const response = await axios.get(`${API}/rdos/consolidado`, {
                params: {
                    empurrador_id: filters.empurrador_id,
                    data_inicio: filters.data_inicio,
                    data_fim: filters.data_fim
                }
            });
            setConsolidado(response.data);
            setConsolidadoDialogOpen(true);
        } catch (error) {
            toast.error('Erro ao gerar consolidado');
        }
    };

    const handleDeleteRDO = async (rdoId, numeroRdo) => {
        if (!window.confirm(`Tem certeza que deseja excluir o lançamento ${numeroRdo}?\n\nEsta ação não pode ser desfeita.`)) {
            return;
        }
        
        try {
            await axios.delete(`${API}/rdos/${rdoId}`);
            toast.success('RDO excluído com sucesso');
            fetchRDOs();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao excluir RDO');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.conferente.trim()) {
            toast.error('Nome do Conferente é obrigatório');
            return;
        }

        if (!formData.empurrador_id) {
            toast.error('Selecione a embarcação');
            return;
        }

        try {
            await axios.post(`${API}/rdos`, formData);
            toast.success('RDO criado com sucesso!');
            setDialogOpen(false);
            resetForm();
            fetchRDOs();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao criar RDO');
        }
    };

    const resetForm = () => {
        setFormData({
            empurrador_id: '',
            viagem_id: '',
            conferente: '',
            data_lancamento: new Date().toISOString().split('T')[0],
            abastecimento_recebido: 0,
            tanque_armazenamento: 0,
            tanque_consumo: 0,
            consumo_diario: 0,
            agua_potavel: 0,
            consumo_agua_diario: 0,
            observacoes: '',
            observacao_variacao: '',
            is_novo_abastecimento: false
        });
    };

    const getEmpurradorNome = (id) => empurradores.find(e => e.id === id)?.nome || '-';

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
    };

    const formatNumber = (num) => {
        return num?.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) || '0';
    };

    const canEdit = hasRole(['admin', 'gestor', 'operador']);

    // Calcular total de combustível em tempo real
    const totalCombustivel = parseFloat(formData.tanque_armazenamento || 0) + parseFloat(formData.tanque_consumo || 0);

    if (loading && rdos.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="rdo-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        RDO - Relatório Diário de Operações
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Controle de combustível, água e tanques com rastreabilidade
                    </p>
                </div>
                
                <div className="flex gap-2">
                    {filters.empurrador_id && filters.data_inicio && filters.data_fim && (
                        <Button 
                            onClick={fetchConsolidado}
                            variant="outline"
                            className="border-cyan-600 text-cyan-400 hover:bg-cyan-600/20 rounded-sm"
                        >
                            <BarChart3 className="w-4 h-4 mr-2" />
                            Consolidado
                        </Button>
                    )}
                    
                    {canEdit && (
                        <Dialog open={dialogOpen} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) resetForm();
                        }}>
                            <DialogTrigger asChild>
                                <Button 
                                    data-testid="btn-new-rdo"
                                    className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Novo RDO
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-cyan-400" />
                                        Novo RDO - Relatório Diário de Operações
                                    </DialogTitle>
                                </DialogHeader>
                                
                                <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                                    {/* Identificação */}
                                    <div className="p-4 bg-slate-800/50 rounded-sm space-y-4">
                                        <h3 className="text-sm font-mono uppercase tracking-wider text-cyan-400 flex items-center gap-2">
                                            <Calendar className="w-4 h-4" />
                                            Identificação
                                        </h3>
                                        
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                    Embarcação *
                                                </Label>
                                                <Select 
                                                    value={formData.empurrador_id || "none"} 
                                                    onValueChange={(value) => {
                                                        setFormData({...formData, empurrador_id: value === "none" ? "" : value});
                                                        if (value !== "none") {
                                                            fetchProjecao(value);
                                                            fetchGrafico(value);
                                                        }
                                                    }}
                                                >
                                                    <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                        <SelectValue placeholder="Selecione..." />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-slate-900 border-slate-800">
                                                        <SelectItem value="none">Selecione a embarcação</SelectItem>
                                                        {empurradores.filter(e => e.status === 'ativo').map((emp) => (
                                                            <SelectItem key={emp.id} value={emp.id}>{emp.nome}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            
                                            <div className="space-y-2">
                                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                    Viagem Vinculada
                                                </Label>
                                                <Select 
                                                    value={formData.viagem_id || "none"} 
                                                    onValueChange={(value) => setFormData({...formData, viagem_id: value === "none" ? "" : value})}
                                                >
                                                    <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                        <SelectValue placeholder="Opcional" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-slate-900 border-slate-800">
                                                        <SelectItem value="none">Nenhuma</SelectItem>
                                                        {viagens.map((v) => (
                                                            <SelectItem key={v.id} value={v.id}>{v.numero_viagem}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                    Conferente *
                                                </Label>
                                                <Input
                                                    value={formData.conferente}
                                                    onChange={(e) => setFormData({...formData, conferente: e.target.value})}
                                                    required
                                                    placeholder="Nome do conferente"
                                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                                />
                                            </div>
                                            
                                            <div className="space-y-2">
                                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                    Data do Lançamento *
                                                </Label>
                                                <Input
                                                    type="date"
                                                    value={formData.data_lancamento}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    onChange={(e) => setFormData({...formData, data_lancamento: e.target.value})}
                                                    required
                                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Abastecimento */}
                                    <div className="p-4 bg-slate-800/50 rounded-sm space-y-4">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-sm font-mono uppercase tracking-wider text-amber-400 flex items-center gap-2">
                                                <RefreshCw className="w-4 h-4" />
                                                Novo Abastecimento
                                            </h3>
                                            <div className="flex items-center gap-2">
                                                <Switch
                                                    checked={formData.is_novo_abastecimento}
                                                    onCheckedChange={(checked) => setFormData({...formData, is_novo_abastecimento: checked})}
                                                />
                                                <span className="text-xs text-slate-400">Registrar abastecimento</span>
                                            </div>
                                        </div>
                                        
                                        {formData.is_novo_abastecimento && (
                                            <div className="space-y-2">
                                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                    Quantidade Recebida (Litros)
                                                </Label>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={formData.abastecimento_recebido}
                                                    onChange={(e) => setFormData({...formData, abastecimento_recebido: parseFloat(e.target.value) || 0})}
                                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                                />
                                                <p className="text-xs text-amber-400">Este valor será o novo marco base de controle</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Combustível */}
                                    <div className="p-4 bg-slate-800/50 rounded-sm space-y-4">
                                        <h3 className="text-sm font-mono uppercase tracking-wider text-orange-400 flex items-center gap-2">
                                            <Fuel className="w-4 h-4" />
                                            Controle de Combustível
                                        </h3>
                                        
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="space-y-2">
                                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                    Tanque Armazenamento (L)
                                                </Label>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={formData.tanque_armazenamento}
                                                    onChange={(e) => setFormData({...formData, tanque_armazenamento: parseFloat(e.target.value) || 0})}
                                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                                />
                                            </div>
                                            
                                            <div className="space-y-2">
                                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                    Tanque Consumo (L)
                                                </Label>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={formData.tanque_consumo}
                                                    onChange={(e) => setFormData({...formData, tanque_consumo: parseFloat(e.target.value) || 0})}
                                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                                />
                                            </div>
                                            
                                            <div className="space-y-2">
                                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                    Total Combustível (L)
                                                </Label>
                                                <Input
                                                    type="number"
                                                    value={totalCombustivel}
                                                    disabled
                                                    className="bg-slate-800 border-slate-700 text-cyan-400 font-bold rounded-sm cursor-not-allowed"
                                                />
                                                <p className="text-xs text-slate-500">Calculado automaticamente</p>
                                            </div>
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Consumo Diário (L)
                                            </Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={formData.consumo_diario}
                                                onChange={(e) => setFormData({...formData, consumo_diario: parseFloat(e.target.value) || 0})}
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                    </div>

                                    {/* Água */}
                                    <div className="p-4 bg-slate-800/50 rounded-sm space-y-4">
                                        <h3 className="text-sm font-mono uppercase tracking-wider text-blue-400 flex items-center gap-2">
                                            <Droplets className="w-4 h-4" />
                                            Controle de Água Potável
                                        </h3>
                                        
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                    Estoque Atual (L)
                                                </Label>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={formData.agua_potavel}
                                                    onChange={(e) => setFormData({...formData, agua_potavel: parseFloat(e.target.value) || 0})}
                                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                                />
                                            </div>
                                            
                                            <div className="space-y-2">
                                                <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                    Consumo Diário (L)
                                                </Label>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={formData.consumo_agua_diario}
                                                    onChange={(e) => setFormData({...formData, consumo_agua_diario: parseFloat(e.target.value) || 0})}
                                                    className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Observações */}
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Observações Gerais
                                            </Label>
                                            <Textarea
                                                value={formData.observacoes}
                                                onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                                                placeholder="Observações adicionais..."
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[60px]"
                                            />
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono flex items-center gap-2">
                                                <AlertTriangle className="w-4 h-4 text-amber-400" />
                                                Justificativa de Variação (obrigatória se variação &gt; 5%)
                                            </Label>
                                            <Textarea
                                                value={formData.observacao_variacao}
                                                onChange={(e) => setFormData({...formData, observacao_variacao: e.target.value})}
                                                placeholder="Justifique variações significativas no consumo..."
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[60px]"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex gap-3 pt-4">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => {
                                                setDialogOpen(false);
                                                resetForm();
                                            }}
                                            className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            type="submit"
                                            className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                        >
                                            Registrar RDO
                                        </Button>
                                    </div>
                                </form>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
            </div>

            {/* Filtros */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-4">
                        <Filter className="w-4 h-4 text-cyan-400" />
                        <span className="text-sm font-mono uppercase tracking-wider text-cyan-400">Filtros</span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Embarcação</Label>
                            <Select 
                                value={filters.empurrador_id || "all"} 
                                onValueChange={(value) => {
                                    setFilters({...filters, empurrador_id: value === "all" ? "" : value});
                                    if (value !== "all") {
                                        fetchProjecao(value);
                                        fetchGrafico(value);
                                    } else {
                                        setProjecao(null);
                                        setGraficoData(null);
                                    }
                                }}
                            >
                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-800">
                                    <SelectItem value="all">Todos</SelectItem>
                                    {empurradores.map((emp) => (
                                        <SelectItem key={emp.id} value={emp.id}>{emp.nome}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Data Início</Label>
                            <Input
                                type="date"
                                value={filters.data_inicio}
                                onChange={(e) => setFilters({...filters, data_inicio: e.target.value})}
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Data Fim</Label>
                            <Input
                                type="date"
                                value={filters.data_fim}
                                onChange={(e) => setFilters({...filters, data_fim: e.target.value})}
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Status</Label>
                            <Select 
                                value={filters.status_combustivel || "all"} 
                                onValueChange={(value) => setFilters({...filters, status_combustivel: value === "all" ? "" : value})}
                            >
                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-800">
                                    <SelectItem value="all">Todos</SelectItem>
                                    <SelectItem value="normal">🟢 Normal</SelectItem>
                                    <SelectItem value="atencao">🟡 Atenção</SelectItem>
                                    <SelectItem value="critico">🔴 Crítico</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-end">
                            <Button 
                                onClick={() => {
                                    setFilters({ empurrador_id: '', data_inicio: '', data_fim: '', status_combustivel: '' });
                                    setProjecao(null);
                                    setGraficoData(null);
                                }}
                                variant="outline"
                                className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm h-9"
                            >
                                Limpar
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Projeção de Autonomia */}
            {projecao && projecao.status !== 'sem_dados' && (
                <Card className={`rounded-sm border ${
                    projecao.status === 'normal' ? 'bg-green-500/10 border-green-500/30' :
                    projecao.status === 'atencao' ? 'bg-yellow-500/10 border-yellow-500/30' :
                    'bg-red-500/10 border-red-500/30'
                }`}>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-slate-900/50 rounded-sm">
                                    <Gauge className={`w-8 h-8 ${
                                        projecao.status === 'normal' ? 'text-green-400' :
                                        projecao.status === 'atencao' ? 'text-yellow-400' :
                                        'text-red-400'
                                    }`} />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-400 font-mono uppercase">Projeção de Autonomia</p>
                                    <p className="text-2xl font-bold text-white">
                                        {projecao.dias_autonomia} dias
                                    </p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-8">
                                <div className="text-right">
                                    <p className="text-xs text-slate-400">Saldo Atual</p>
                                    <p className="text-lg font-bold text-white">{formatNumber(projecao.saldo_atual)} L</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-slate-400">Média Consumo/Dia</p>
                                    <p className="text-lg font-bold text-white">{formatNumber(projecao.media_consumo_diario)} L</p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Gráfico */}
            {graficoData && graficoData.dados.length > 0 && (
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-mono uppercase tracking-wider text-cyan-400 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Evolução de Consumo (Últimos 30 dias)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={250}>
                            <LineChart data={graficoData.dados}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                <XAxis 
                                    dataKey="data" 
                                    stroke="#64748b" 
                                    tick={{ fill: '#64748b', fontSize: 10 }}
                                    tickFormatter={(value) => new Date(value + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                />
                                <YAxis stroke="#64748b" tick={{ fill: '#64748b', fontSize: 10 }} />
                                <Tooltip 
                                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                                    labelStyle={{ color: '#94a3b8' }}
                                />
                                <Legend />
                                <ReferenceLine 
                                    y={graficoData.media_consumo} 
                                    stroke="#f59e0b" 
                                    strokeDasharray="5 5" 
                                    label={{ value: 'Média', fill: '#f59e0b', fontSize: 10 }}
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="consumo" 
                                    name="Consumo (L)" 
                                    stroke="#f97316" 
                                    strokeWidth={2}
                                    dot={{ fill: '#f97316', strokeWidth: 2 }}
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="remanescente" 
                                    name="Saldo (L)" 
                                    stroke="#22d3ee" 
                                    strokeWidth={2}
                                    dot={{ fill: '#22d3ee', strokeWidth: 2 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-blue-500/20 rounded-sm">
                            <FileText className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">{rdos.length}</p>
                            <p className="text-xs text-slate-400">Total RDOs</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-green-500/20 rounded-sm">
                            <Fuel className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">
                                {rdos.filter(r => r.status_combustivel === 'normal').length}
                            </p>
                            <p className="text-xs text-slate-400">🟢 Normais</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-yellow-500/20 rounded-sm">
                            <AlertTriangle className="w-5 h-5 text-yellow-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">
                                {rdos.filter(r => r.status_combustivel === 'atencao').length}
                            </p>
                            <p className="text-xs text-slate-400">🟡 Atenção</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-red-500/20 rounded-sm">
                            <TrendingDown className="w-5 h-5 text-red-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">
                                {rdos.filter(r => r.status_combustivel === 'critico').length}
                            </p>
                            <p className="text-xs text-slate-400">🔴 Críticos</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Table */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-0">
                    {rdos.length === 0 ? (
                        <div className="p-12 text-center">
                            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">Nenhum RDO encontrado</p>
                        </div>
                    ) : (
                        <Table className="table-naval">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Nº RDO</TableHead>
                                    <TableHead>Data</TableHead>
                                    <TableHead>Embarcação</TableHead>
                                    <TableHead>Conferente</TableHead>
                                    <TableHead>Combustível</TableHead>
                                    <TableHead>Consumo</TableHead>
                                    <TableHead>Saldo</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Ações</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rdos.map((rdo) => (
                                    <TableRow key={rdo.id}>
                                        <TableCell className="font-mono text-cyan-400 font-bold">
                                            <div className="flex items-center gap-1">
                                                {rdo.numero_rdo}
                                                {rdo.bloqueado && <Lock className="w-3 h-3 text-slate-500" />}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-slate-400 text-sm">
                                            {formatDate(rdo.data_lancamento)}
                                        </TableCell>
                                        <TableCell className="text-white">
                                            {getEmpurradorNome(rdo.empurrador_id)}
                                        </TableCell>
                                        <TableCell className="text-slate-300">
                                            {rdo.conferente}
                                        </TableCell>
                                        <TableCell className="text-orange-400 font-mono">
                                            {formatNumber(rdo.total_combustivel)} L
                                        </TableCell>
                                        <TableCell className="text-amber-400 font-mono">
                                            {formatNumber(rdo.consumo_diario)} L
                                            {rdo.variacao_consumo_percentual !== 0 && (
                                                <span className={`ml-1 text-xs ${
                                                    rdo.variacao_consumo_percentual > 0 ? 'text-red-400' : 'text-green-400'
                                                }`}>
                                                    ({rdo.variacao_consumo_percentual > 0 ? '+' : ''}{rdo.variacao_consumo_percentual}%)
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-cyan-400 font-mono font-bold">
                                            {formatNumber(rdo.remanescente_combustivel)} L
                                        </TableCell>
                                        <TableCell>
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${statusLabels[rdo.status_combustivel]?.class || 'badge-info'}
                                            `}>
                                                {statusLabels[rdo.status_combustivel]?.icon} {statusLabels[rdo.status_combustivel]?.label}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedRDO(rdo);
                                                        setDetailDialogOpen(true);
                                                    }}
                                                    className="text-slate-400 hover:text-white hover:bg-slate-800"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                {hasRole(['admin']) && !rdo.bloqueado && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleDeleteRDO(rdo.id, rdo.numero_rdo);
                                                        }}
                                                        className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <FileText className="w-5 h-5 text-cyan-400" />
                            {selectedRDO?.numero_rdo}
                            {selectedRDO?.bloqueado && (
                                <span className="ml-2 px-2 py-0.5 bg-slate-700 text-slate-400 text-xs rounded-sm flex items-center gap-1">
                                    <Lock className="w-3 h-3" /> Bloqueado
                                </span>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedRDO && (
                        <div className="space-y-6 mt-4">
                            {/* Info Geral */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Data</p>
                                    <p className="text-white mt-1">{formatDate(selectedRDO.data_lancamento)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Conferente</p>
                                    <p className="text-white mt-1">{selectedRDO.conferente}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Embarcação</p>
                                    <p className="text-white mt-1">{getEmpurradorNome(selectedRDO.empurrador_id)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Status</p>
                                    <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-mono uppercase ${statusLabels[selectedRDO.status_combustivel]?.class}`}>
                                        {statusLabels[selectedRDO.status_combustivel]?.icon} {statusLabels[selectedRDO.status_combustivel]?.label}
                                    </span>
                                </div>
                            </div>

                            {/* Combustível */}
                            <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-sm">
                                <h4 className="text-sm font-mono uppercase text-orange-400 mb-3 flex items-center gap-2">
                                    <Fuel className="w-4 h-4" /> Combustível
                                </h4>
                                <div className="grid grid-cols-3 gap-4 text-center">
                                    <div>
                                        <p className="text-xs text-slate-400">Tanque Armazenamento</p>
                                        <p className="text-xl font-bold text-white">{formatNumber(selectedRDO.tanque_armazenamento)} L</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400">Tanque Consumo</p>
                                        <p className="text-xl font-bold text-white">{formatNumber(selectedRDO.tanque_consumo)} L</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400">Total</p>
                                        <p className="text-xl font-bold text-orange-400">{formatNumber(selectedRDO.total_combustivel)} L</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-4 text-center mt-4 pt-4 border-t border-orange-500/30">
                                    <div>
                                        <p className="text-xs text-slate-400">Consumo Diário</p>
                                        <p className="text-lg font-bold text-amber-400">{formatNumber(selectedRDO.consumo_diario)} L</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400">Variação</p>
                                        <p className={`text-lg font-bold ${
                                            selectedRDO.variacao_consumo_percentual > 5 ? 'text-red-400' :
                                            selectedRDO.variacao_consumo_percentual < -5 ? 'text-green-400' : 'text-white'
                                        }`}>
                                            {selectedRDO.variacao_consumo_percentual > 0 ? '+' : ''}{selectedRDO.variacao_consumo_percentual}%
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400">Saldo Remanescente</p>
                                        <p className="text-lg font-bold text-cyan-400">{formatNumber(selectedRDO.remanescente_combustivel)} L</p>
                                    </div>
                                </div>
                            </div>

                            {/* Água */}
                            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                                <h4 className="text-sm font-mono uppercase text-blue-400 mb-3 flex items-center gap-2">
                                    <Droplets className="w-4 h-4" /> Água Potável
                                </h4>
                                <div className="grid grid-cols-3 gap-4 text-center">
                                    <div>
                                        <p className="text-xs text-slate-400">Estoque</p>
                                        <p className="text-xl font-bold text-white">{formatNumber(selectedRDO.agua_potavel)} L</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400">Consumo Diário</p>
                                        <p className="text-xl font-bold text-white">{formatNumber(selectedRDO.consumo_agua_diario)} L</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-400">Saldo</p>
                                        <p className={`text-xl font-bold ${selectedRDO.status_agua === 'critico' ? 'text-red-400' : 'text-blue-400'}`}>
                                            {formatNumber(selectedRDO.remanescente_agua)} L
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Observações */}
                            {(selectedRDO.observacoes || selectedRDO.observacao_variacao) && (
                                <div className="space-y-3">
                                    {selectedRDO.observacoes && (
                                        <div>
                                            <p className="text-xs text-slate-500 font-mono uppercase mb-1">Observações</p>
                                            <p className="text-slate-300 bg-slate-800/50 p-3 rounded-sm">{selectedRDO.observacoes}</p>
                                        </div>
                                    )}
                                    {selectedRDO.observacao_variacao && (
                                        <div>
                                            <p className="text-xs text-amber-400 font-mono uppercase mb-1 flex items-center gap-1">
                                                <AlertTriangle className="w-3 h-3" /> Justificativa de Variação
                                            </p>
                                            <p className="text-slate-300 bg-amber-500/10 border border-amber-500/30 p-3 rounded-sm">
                                                {selectedRDO.observacao_variacao}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Log de Alterações */}
                            {selectedRDO.alteracoes && selectedRDO.alteracoes.length > 0 && (
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase mb-2">Histórico de Alterações</p>
                                    <div className="space-y-2 max-h-[150px] overflow-y-auto">
                                        {selectedRDO.alteracoes.map((alt, idx) => (
                                            <div key={idx} className="text-xs bg-slate-800/50 p-2 rounded-sm">
                                                <span className="text-slate-500">{new Date(alt.data).toLocaleString('pt-BR')}</span>
                                                <span className="text-slate-400 mx-2">|</span>
                                                <span className="text-cyan-400">{alt.usuario}</span>
                                                <span className="text-slate-400 mx-2">alterou</span>
                                                <span className="text-white">{alt.campo}</span>
                                                <span className="text-slate-400 mx-1">de</span>
                                                <span className="text-red-400">{alt.valor_anterior}</span>
                                                <span className="text-slate-400 mx-1">para</span>
                                                <span className="text-green-400">{alt.valor_novo}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Consolidado Dialog */}
            <Dialog open={consolidadoDialogOpen} onOpenChange={setConsolidadoDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-cyan-400" />
                            Relatório Consolidado
                        </DialogTitle>
                    </DialogHeader>
                    
                    {consolidado && (
                        <div className="space-y-6 mt-4">
                            <div className={`p-4 rounded-sm border ${
                                consolidado.status_final === 'conforme' ? 'bg-green-500/10 border-green-500/30' :
                                consolidado.status_final === 'atencao' ? 'bg-yellow-500/10 border-yellow-500/30' :
                                'bg-red-500/10 border-red-500/30'
                            }`}>
                                <div className="text-center">
                                    <p className="text-xs text-slate-400 font-mono uppercase">Status Final</p>
                                    <p className={`text-2xl font-bold uppercase ${
                                        consolidado.status_final === 'conforme' ? 'text-green-400' :
                                        consolidado.status_final === 'atencao' ? 'text-yellow-400' :
                                        'text-red-400'
                                    }`}>
                                        {consolidado.status_final === 'conforme' ? '✓ Conforme' :
                                         consolidado.status_final === 'atencao' ? '⚠ Atenção' :
                                         '✗ Crítico'}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-slate-800/50 rounded-sm text-center">
                                    <p className="text-xs text-slate-400">Total Abastecido</p>
                                    <p className="text-2xl font-bold text-green-400">{formatNumber(consolidado.total_abastecido)} L</p>
                                </div>
                                <div className="p-4 bg-slate-800/50 rounded-sm text-center">
                                    <p className="text-xs text-slate-400">Total Consumido</p>
                                    <p className="text-2xl font-bold text-orange-400">{formatNumber(consolidado.total_consumido)} L</p>
                                </div>
                                <div className="p-4 bg-slate-800/50 rounded-sm text-center">
                                    <p className="text-xs text-slate-400">Diferença Apurada</p>
                                    <p className={`text-2xl font-bold ${consolidado.diferenca_apurada >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        {consolidado.diferenca_apurada >= 0 ? '+' : ''}{formatNumber(consolidado.diferenca_apurada)} L
                                    </p>
                                </div>
                                <div className="p-4 bg-slate-800/50 rounded-sm text-center">
                                    <p className="text-xs text-slate-400">Variação %</p>
                                    <p className={`text-2xl font-bold ${
                                        consolidado.percentual_variacao <= 2 ? 'text-green-400' :
                                        consolidado.percentual_variacao <= 5 ? 'text-yellow-400' :
                                        'text-red-400'
                                    }`}>
                                        {consolidado.percentual_variacao}%
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-3 bg-slate-800/30 rounded-sm">
                                    <p className="text-xs text-slate-400">Média Consumo Diário</p>
                                    <p className="text-lg font-bold text-white">{formatNumber(consolidado.media_consumo_diario)} L</p>
                                </div>
                                <div className="p-3 bg-slate-800/30 rounded-sm">
                                    <p className="text-xs text-slate-400">Dias Analisados</p>
                                    <p className="text-lg font-bold text-white">{consolidado.dias_analisados} dias</p>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default RDOPage;
